/**
 * BundleModal Component
 * Enterprise-grade reusable modal for displaying bundle details
 */

import React, { useEffect, useState } from 'react';
import { X, Check, Users, Star, Clock, BookOpen, Sparkles, TrendingUp, AlertCircle } from 'lucide-react';
import { Bundle, Course } from '@/types/api.types';
import { bundleService } from '@/services/bundle.service';
import { courseService } from '@/services/course.service';

interface BundleModalProps {
  bundleId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onEnroll?: (bundleId: string) => void;
}

export const BundleModal: React.FC<BundleModalProps> = ({ 
  bundleId, 
  isOpen, 
  onClose, 
  onEnroll 
}) => {
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && bundleId) {
      fetchBundleData();
    }
  }, [isOpen, bundleId]);

  const fetchBundleData = async () => {
    if (!bundleId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch bundle details
      const bundleResponse = await bundleService.getBundleById(bundleId);
      
      if (!bundleResponse.success || !bundleResponse.data) {
        throw new Error(bundleResponse.error || 'Failed to load bundle');
      }
      
      setBundle(bundleResponse.data);
      
      // Fetch associated courses
      if (bundleResponse.data.course_ids && bundleResponse.data.course_ids.length > 0) {
        const coursePromises = bundleResponse.data.course_ids.map(courseId => 
          courseService.getCourseById(courseId)
        );
        
        const courseResponses = await Promise.all(coursePromises);
        const fetchedCourses = courseResponses
          .filter(response => response.success && response.data)
          .map(response => response.data as Course);
        
        setCourses(fetchedCourses);
      }
    } catch (err) {
      console.error('Error fetching bundle data:', err);
      setError('Failed to load bundle details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = () => {
    if (bundle && onEnroll) {
      onEnroll(bundle.id);
    }
  };

  const handleClose = () => {
    setBundle(null);
    setCourses([]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const savingsAmount = bundle?.full_price && bundle?.discount_price 
    ? bundle.full_price - bundle.discount_price 
    : 0;
  
  const actualSavingsPercentage = bundle?.full_price && bundle?.discount_price
    ? Math.round((savingsAmount / bundle.full_price) * 100)
    : bundle?.savings_percentage || 0;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div 
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-900 to-black rounded-3xl border border-white/20 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-all hover:rotate-90 duration-300"
          aria-label="Close modal"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center p-20">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
              <p className="text-gray-400">Loading bundle details...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="flex items-center justify-center p-20">
            <div className="flex flex-col items-center space-y-4 max-w-md">
              <AlertCircle className="w-16 h-16 text-red-400" />
              <p className="text-red-400 text-center">{error}</p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {bundle && !loading && !error && (
          <>
            {/* Header Section */}
            <div className="relative p-8 pb-6 border-b border-white/10">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-blue-600/10"></div>
              <div className="relative">
                {/* Badge */}
                {actualSavingsPercentage > 0 && (
                  <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mb-4">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-semibold">Save {actualSavingsPercentage}%</span>
                  </div>
                )}

                <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {bundle.title}
                </h2>
                
                <p className="text-gray-400 text-lg leading-relaxed max-w-3xl">
                  {bundle.description}
                </p>

                {/* Stats */}
                <div className="flex items-center gap-6 mt-6 flex-wrap">
                  <div className="flex items-center space-x-2">
                    <BookOpen className="w-5 h-5 text-purple-400" />
                    <span className="text-sm text-gray-300">
                      {courses.length} {courses.length === 1 ? 'Topic' : 'Topics'}
                    </span>
                  </div>
                  
                  {courses.length > 0 && (
                    <>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-5 h-5 text-blue-400" />
                        <span className="text-sm text-gray-300">
                          {courses.reduce((total, course) => total + (course.duration_hours || 0), 0)} hours
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                        <span className="text-sm text-gray-300">Lifetime Access</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Courses List */}
            <div className="p-8">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                <BookOpen className="w-6 h-6 text-purple-400" />
                <span>Included Courses</span>
              </h3>

              {courses.length > 0 ? (
                <div className="space-y-3 mb-8">
                  {courses.map((course, index) => (
                    <div 
                      key={course.id}
                      className="flex items-start space-x-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 hover:border-purple-500/30 transition-all group"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-lg font-bold">{index + 1}</span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-white mb-1 group-hover:text-purple-400 transition-colors">
                          {course.title}
                        </h4>
                        <p className="text-sm text-gray-400 line-clamp-2">
                          {course.description}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          {course.category && (
                            <span className="text-xs text-gray-500 uppercase tracking-wider">
                              {course.category}
                            </span>
                          )}
                          {course.duration_hours && (
                            <span className="text-xs text-gray-500">
                              {course.duration_hours}h
                            </span>
                          )}
                          {course.skill_level && (
                            <span className="text-xs text-gray-500">
                              {course.skill_level}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex-shrink-0">
                        <Check className="w-5 h-5 text-green-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">No courses found in this bundle.</p>
              )}

              {/* Benefits Section */}
              <div className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-2xl p-6 border border-white/10 mb-8">
                <h4 className="font-semibold text-lg text-white mb-4 flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  <span>What You'll Get</span>
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Lifetime Access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Certificate of Completion</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Community Access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Regular Updates</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with Pricing */}
            <div className="sticky bottom-0 p-8 pt-6 border-t border-white/10 bg-gradient-to-t from-gray-900 to-transparent backdrop-blur-sm">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-baseline gap-3 mb-1">
                    <span className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                      ${bundle.discount_price || bundle.full_price}
                    </span>
                    {bundle.discount_price && bundle.discount_price < bundle.full_price && (
                      <span className="text-xl text-gray-500 line-through">
                        ${bundle.full_price}
                      </span>
                    )}
                  </div>
                  {savingsAmount > 0 && (
                    <p className="text-sm text-green-400">
                      Save ${savingsAmount.toFixed(2)} with this bundle
                    </p>
                  )}
                </div>

                <button
                  onClick={handleEnroll}
                  className="px-10 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-purple-500/50 transition-all hover:scale-105 flex items-center space-x-2"
                >
                  <span>Enroll Now</span>
                  <Sparkles className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BundleModal;
